import React, { useContext, useState } from "react";
import { Alert } from "reactstrap";
import Popup from "src/Components/Popup/popup";
import Form from "src/Components/Form";
import __ from "src/utils/Translations";
import { dynamicNotification } from "src/utils/Notifications";
import { employeeAccountPermissionWrite } from "src/utils/RoleBasedSecurity/permissions";
import newRelicErrorReport from "src/utils/newRelic/newRelicErrorReport";
import RbsContext from "src/utils/RoleBasedSecurity/RbsContext";
import { chargeOrDischarge } from "src/Pages/Company/EmployeeManagement/CompanyEmployees/Edit/ChargeUps/chargeOrDischargeAccount";
import { usePermission } from "src/utils/hooks/permissions/usePermissions";
import { ChargeUpsRow } from "src/Pages/Company/EmployeeManagement/CompanyEmployees/Edit/ChargeUps/ChargeUps.models";

type OneTimeChargeModalProps = Readonly<{
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  account: ChargeUpsRow;
  employeeId: string;
}>;

export default function OneTimeChargeModal({
  isOpen,
  onClose,
  onSuccess,
  account,
  employeeId,
}: OneTimeChargeModalProps) {
  const [amount, setAmount] = useState<string | null>(null);
  const { userInfo } = useContext(RbsContext);
  const hasEmployeeAccountWritePermission = usePermission(employeeAccountPermissionWrite);

  const isAhr = userInfo.isAhr();
  const isInAdvanceBank = account.pointsBank.settlementType === "IN_ADVANCE";
  const dischargeBlocked = isAhr && isInAdvanceBank;

  const submit = async () => {
    try {
      await chargeOrDischarge(account.accountId, Number.parseFloat(amount!), {
        isAhr,
        principalId: userInfo.getId(),
        employeeId,
      });
      dynamicNotification(__("Pomyślnie zaktualizowano liczbę punktów."));
      onSuccess();
      onClose();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      newRelicErrorReport(e, "ChargeUps/modals/OneTimeChargeModal.tsx");
      const message = e instanceof Error ? e.message : __("Nie udało się doładować konta.");
      dynamicNotification(message, "error");
    }
  };

  return (
    <Popup
      id="oneTimeChargeModal"
      isOpen={isOpen}
      toggle={onClose}
      unmountOnClose
      size="md"
    >
      {dischargeBlocked && (
        <Alert color="warning">
          {__("Banki rozliczane z góry nie mogą być rozładowywane.")}
        </Alert>
      )}
      <Form
        id="oneTimeChargeForm"
        data={{ amount }}
        config={{
          isInPopup: true,
          togglePopup: onClose,
          title: __("Doładuj/Rozładuj jednorazowo"),
          onSubmit: submit,
          defaultOnChange: (_key: string, value: string | null) =>
            setAmount(value),
          buttons: [
            { onClick: onClose, text: __("Zamknij"), color: "light" },
            {
              text: __("Wykonaj"),
              type: "submit",
              disabled: !hasEmployeeAccountWritePermission,
            },
          ],
          noCards: true,
          formGroups: [
            {
              formElements: [
                {
                  id: "amount",
                  dataId: "amount",
                  label: __("Wartość operacji"),
                  type: "text",
                  valueFormatter: dischargeBlocked ? "price" : "price_with_negative",
                  validation: [
                    "requiredNumericField",
                    ...(dischargeBlocked
                      ? []
                      : [{ method: "greaterEqualThan", args: [-account.balance] }]),
                    { method: "lessEqualThanForTopUpAmount", args: [10000, isAhr
                      ? "Kwota zasilenia jest za wysoka, aby wykonać to zasilenie skontaktuj się z Opiekunem"
                      : "Kwota jednorazowego zasilenia przez OMB nie może przekraczać 10.000"
                    ] },
                  ],
                },
              ],
            },
          ],
        }}
      />
    </Popup>
  );
}
